import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * CoverPreview — aperçu d'une page de yearbook (typiquement la 1ère de couverture),
 * rendu depuis un `objects_position` (JSON brut Rails, snake_case) ou un objet
 * déjà normalisé (camelCase).
 *
 * FICHIER UNIQUE ET AUTONOME : styles inline uniquement, aucune dépendance
 * Tailwind/shadcn/lucide-react ni import interne — copiable tel quel dans un
 * autre projet.
 *
 * Utilisation :
 *   <CoverPreview
 *     page={album.objects_position}              // ou l'objet page complet
 *     assetsBaseUrl="https://zephir.1year1book.com"
 *     pageWidth={book.page_width}                // dimensions réelles (recommandé)
 *     pageHeight={book.page_height}
 *     aspectRatio={1}                            // largeur / hauteur (secours)
 *     maxHeight={300}
 *   />
 *
 * ⚠️ Les polices (/fonts/*.woff2) et images sont servies par le Rails : le
 * projet hôte doit autoriser le CORS sur ces routes, sinon les textes tombent
 * en police de secours.
 */

// ============================== Types ==============================

interface PreviewPosition { x: number; y: number }
interface PreviewSize { width: number; height: number }

interface PreviewTextStyle {
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  color: string;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  italic: boolean;
  underline: boolean;
}

interface PreviewCornerRadii { tl?: number; tr?: number; br?: number; bl?: number }

interface PreviewElementShadow {
  offsetX: number;
  offsetY: number;
  blur: number;
  color: string;
}

function elementShadowToCss(s?: PreviewElementShadow, scale: number = 1): string | undefined {
  if (!s) return undefined;
  return `${s.offsetX * scale}px ${s.offsetY * scale}px ${s.blur * scale}px ${s.color}`;
}

function cornerRadiiToCss(cr?: PreviewCornerRadii, scale: number = 1): string | undefined {
  if (!cr) return undefined;
  const { tl = 0, tr = 0, br = 0, bl = 0 } = cr;
  if (!tl && !tr && !br && !bl) return undefined;
  return `${tl * scale}px ${tr * scale}px ${br * scale}px ${bl * scale}px`;
}

interface PreviewElement {
  id: string;
  type: 'text' | 'image' | 'slot' | 'free_slot' | 'shape' | 'shape_border';
  position: PreviewPosition;
  size: PreviewSize;
  rotation: number;
  content?: string;
  textStyle?: PreviewTextStyle;
  imageUrl?: string;
  imageRatio?: number;
  imageOffset?: PreviewPosition;
  imageScale?: number;
  fromLayout?: boolean;
  fromBackground?: boolean;
  backgroundSourceId?: number;
  userAdded?: boolean;
  pictureId?: number;
  userId?: number;
  locked?: boolean;
  isSticker?: boolean;
  shapeType?: 'rect' | 'line' | 'circle';
  zPriority?: 'front' | 'back';
  thumbnail?: string;
  previewImageUrl?: string;
  isCircle?: boolean;
  backgroundColor?: string;
  qrcodeId?: number;
  borderRadius?: number;
  strokeWidth?: number;
  forceDimension?: boolean;
  cornerRadii?: PreviewCornerRadii;
  borderColor?: string;
  shadow?: PreviewElementShadow;
  zIndex?: number;
  groupId?: string;
  stickerFormat?: 'png' | 'svg' | 'simple_svg';
  stickerColor?: string;
  forbiddenWords?: string[];
}

interface PreviewPageBackground {
  type: 'color' | 'image';
  value: string;
  trame_background_id?: number | null;
  trame_background_custom_id?: number | null;
  richBackgroundId?: number | null;
}

export interface PreviewPage {
  id: string;
  pageNumber?: number;
  elements: PreviewElement[];
  background: PreviewPageBackground;
  coverType?: 'front' | 'back' | 'tranche' | null;
  name?: string | null;
  /** Largeur personnalisée en pt (tranche). Défaut : 595. */
  pageWidth?: number;
}

const PREVIEW_PAGE_WIDTH = 595;
const PREVIEW_PAGE_HEIGHT = 842;

/** Fond perdu (5 mm en pt) appliqué aux fonds image venant des trames. */
const PREVIEW_BG_BLEED = 14.1732;

// ============================ Normalisation ============================

/**
 * Normalise les éléments bruts de l'API (snake_case) vers le format camelCase
 * attendu par le rendu. Accepte aussi des éléments déjà normalisés.
 */
function normalizeElements(elements: any[]): PreviewElement[] {
  return (elements ?? []).map(normalizeElement);
}

function normalizeElement(el: any): PreviewElement {
  const result = { ...el };

  if (el.corner_radii && !el.cornerRadii) {
    const raw = el.corner_radii;
    result.cornerRadii = (typeof raw === 'string' ? JSON.parse(raw) : raw) as PreviewCornerRadii;
    delete result.corner_radii;
  }
  if (result.cornerRadii && typeof result.cornerRadii === 'string') {
    try { result.cornerRadii = JSON.parse(result.cornerRadii) as PreviewCornerRadii; } catch { delete result.cornerRadii; }
  }

  if (el.image_url !== undefined && el.imageUrl === undefined) { result.imageUrl = el.image_url; delete result.image_url; }
  if (el.image_ratio !== undefined && el.imageRatio === undefined) { result.imageRatio = el.image_ratio; delete result.image_ratio; }
  if (el.image_offset !== undefined && el.imageOffset === undefined) { result.imageOffset = el.image_offset; delete result.image_offset; }
  if (el.image_scale !== undefined && el.imageScale === undefined) { result.imageScale = el.image_scale; delete result.image_scale; }
  if (el.from_layout !== undefined && el.fromLayout === undefined) { result.fromLayout = el.from_layout; delete result.from_layout; }
  if (el.from_background !== undefined && el.fromBackground === undefined) { result.fromBackground = el.from_background === true || el.from_background === 'true'; delete result.from_background; }
  if (el.background_source_id !== undefined && el.backgroundSourceId === undefined) { const value = Number(el.background_source_id); result.backgroundSourceId = Number.isFinite(value) ? value : undefined; delete result.background_source_id; }
  if (el.user_added !== undefined && el.userAdded === undefined) { result.userAdded = el.user_added === true || el.user_added === 'true'; delete result.user_added; }

  if (el.user_id !== undefined && el.user_id !== null) {
    if (result.userId === undefined || result.userId === null) {
      const val = typeof el.user_id === 'string' ? parseInt(el.user_id, 10) : el.user_id;
      result.userId = typeof val === 'number' && !isNaN(val) ? val : el.user_id;
    }
    delete result.user_id;
  }
  if (typeof result.userId === 'string') {
    const val = parseInt(result.userId, 10);
    if (!isNaN(result.userId as any)) result.userId = val;
  }

  if (el.picture_id !== undefined && el.pictureId === undefined) { result.pictureId = el.picture_id; delete result.picture_id; }
  if (el.is_circle !== undefined && el.isCircle === undefined) { result.isCircle = el.is_circle === 'true' || el.is_circle === true; delete result.is_circle; }
  if (el.is_sticker !== undefined && el.isSticker === undefined) { result.isSticker = el.is_sticker === 'true' || el.is_sticker === true; delete result.is_sticker; }
  if (el.z_priority !== undefined && el.zPriority === undefined) { result.zPriority = el.z_priority; delete result.z_priority; }
  if (el.text_style !== undefined && el.textStyle === undefined) { result.textStyle = el.text_style; delete result.text_style; }
  if (el.shape_type !== undefined && el.shapeType === undefined) { result.shapeType = el.shape_type; delete result.shape_type; }
  if (el.preview_image_url !== undefined && el.previewImageUrl === undefined) { result.previewImageUrl = el.preview_image_url; delete result.preview_image_url; }
  if (el.background_color !== undefined && el.backgroundColor === undefined) { result.backgroundColor = el.background_color; delete result.background_color; }
  if (el.border_radius !== undefined && el.borderRadius === undefined) { result.borderRadius = el.border_radius; delete result.border_radius; }
  if (el.stroke_width !== undefined && el.strokeWidth === undefined) { result.strokeWidth = el.stroke_width; delete result.stroke_width; }
  if (el.force_dimension !== undefined && el.forceDimension === undefined) { result.forceDimension = el.force_dimension === true || el.force_dimension === 'true'; delete result.force_dimension; }
  if (el.qrcode_id !== undefined && el.qrcodeId === undefined) { result.qrcodeId = el.qrcode_id; delete result.qrcode_id; }

  if (el.z_index !== undefined && el.zIndex === undefined) {
    const val = typeof el.z_index === 'string' ? parseInt(el.z_index, 10) : el.z_index;
    result.zIndex = isNaN(val) ? undefined : val;
    delete result.z_index;
  }
  if (result.zIndex !== undefined && typeof result.zIndex === 'string') {
    const val = parseInt(result.zIndex, 10);
    result.zIndex = isNaN(val) ? undefined : val;
  }

  if (el.group_id !== undefined && el.groupId === undefined) { result.groupId = el.group_id; delete result.group_id; }
  if (el.sticker_format !== undefined && el.stickerFormat === undefined) { result.stickerFormat = el.sticker_format; delete result.sticker_format; }
  if (el.sticker_color !== undefined && el.stickerColor === undefined) { result.stickerColor = el.sticker_color; delete result.sticker_color; }
  if (el.forbidden_words !== undefined && el.forbiddenWords === undefined) { result.forbiddenWords = Array.isArray(el.forbidden_words) ? el.forbidden_words : []; delete result.forbidden_words; }

  return result as PreviewElement;
}

/**
 * Normalise une page complète (JSON brut Rails ou objet déjà normalisé).
 * Gère : elements / objects_position, background snake_case, page_width.
 */
function normalizePage(raw: any): PreviewPage {
  const elements = raw?.elements ?? raw?.objects_position ?? [];
  const bg = raw?.background ?? { type: 'color', value: '#ffffff' };
  return {
    id: raw?.id ?? 'preview',
    pageNumber: raw?.pageNumber ?? raw?.page_number,
    elements: normalizeElements(elements),
    background: {
      type: bg.type ?? (bg.value?.startsWith?.('#') ? 'color' : 'image'),
      value: bg.value ?? '#ffffff',
      trame_background_id: bg.trame_background_id ?? null,
      trame_background_custom_id: bg.trame_background_custom_id ?? null,
      richBackgroundId: bg.richBackgroundId ?? bg.rich_background_id ?? null,
    },
    coverType: raw?.coverType ?? raw?.cover_type ?? null,
    name: raw?.name ?? null,
    pageWidth: raw?.pageWidth ?? raw?.page_width ?? undefined,
  };
}

// ========================== Registre des polices ==========================

/**
 * Les fichiers woff2 sont hébergés sur le serveur Rails. Par défaut les URLs
 * sont relatives (`/fonts/...`) ; passez la prop `assetsBaseUrl` du composant
 * pour préfixer avec le domaine Rails, ex : 'https://zephir.1year1book.com'.
 *
 * Recherche insensible à la casse : "LimeLight", "LIMELIGHT" et "Limelight"
 * chargent tous le même fichier.
 */
interface PreviewFontDef {
  name: string;
  file?: string;
  system?: boolean;
  noBold?: boolean;
  noItalic?: boolean;
}

const REGULAR_ONLY = { noBold: true, noItalic: true } as const;

const PREVIEW_FONTS: PreviewFontDef[] = [
  { name: 'Abril Fatface', file: '/fonts/AbrilFatface-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Alfa Slab One', file: '/fonts/AlfaSlabOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Allura', file: '/fonts/Allura-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Arial', system: true },
  { name: 'Ballet', file: '/fonts/Ballet.woff2', ...REGULAR_ONLY },
  { name: 'Barlow', file: '/fonts/Barlow-Regular.woff2' },
  { name: 'Comic Sans MS', system: true },
  { name: 'Courier New', system: true },
  { name: 'Cozy Autumns', file: '/fonts/CozyAutumns.woff2', ...REGULAR_ONLY },
  { name: 'DM Sans', file: '/fonts/DMSans-Regular.woff2' },
  { name: 'Georgia', system: true },
  { name: 'Gochi Hand', file: '/fonts/GochiHand-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Handlee', file: '/fonts/Handlee-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Iceland', file: '/fonts/Iceland-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Impact', system: true, ...REGULAR_ONLY },
  { name: 'Keania One', file: '/fonts/KeaniaOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Knewave', file: '/fonts/Knewave-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Lato', file: '/fonts/Lato-Regular.woff2' },
  { name: 'Limelight', file: '/fonts/Limelight-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Minion Pro', file: '/fonts/MinionPro-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Mochesa', file: '/fonts/mochesa.woff2', ...REGULAR_ONLY },
  { name: 'Monoton', file: '/fonts/Monoton.woff2', ...REGULAR_ONLY },
  { name: 'Parisienne', file: '/fonts/Parisienne.woff2', ...REGULAR_ONLY },
  { name: 'Park Lane NF', system: true, ...REGULAR_ONLY },
  { name: 'Patrick Hand', file: '/fonts/PatrickHand-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Patua One', file: '/fonts/PatuaOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Permanent Marker', file: '/fonts/PermanentMarker-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Petit Formal Script', file: '/fonts/PetitFormalScript-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Playfair Display', file: '/fonts/PlayfairDisplay-Regular.woff2' },
  { name: 'Roboto', file: '/fonts/Roboto-Regular.woff2' },
  { name: 'Shadows Into Light', file: '/fonts/ShadowsIntoLight-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Titan One', file: '/fonts/TitanOne.woff2', ...REGULAR_ONLY },
  { name: 'Trebuchet MS', system: true },
  { name: 'Ultra', file: '/fonts/Ultra-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Yeseva One', file: '/fonts/YesevaOne-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Zagoth de Birse', file: '/fonts/Zagoth-de-Birse.woff2', ...REGULAR_ONLY },
  { name: 'Almendra', file: '/fonts/Almendra-Regular.woff2' },
  { name: 'Amatic SC', file: '/fonts/AmaticSC-Regular.woff2', noItalic: true },
  { name: 'Atma', file: '/fonts/Atma-Regular.woff2', noItalic: true },
  { name: 'Barlow Condensed', file: '/fonts/BarlowCondensed-Regular.woff2' },
  { name: 'Big Shoulders', file: '/fonts/BigShoulders-Regular.woff2', noItalic: true },
  { name: 'Bitcount Grid Double', file: '/fonts/BitcountGridDouble-Regular.woff2', noItalic: true },
  { name: 'Cabin', file: '/fonts/Cabin-Regular.woff2' },
  { name: 'Cabin Condensed', file: '/fonts/Cabin_Condensed-Regular.woff2' },
  { name: 'Caveat', file: '/fonts/Caveat-Regular.woff2', noItalic: true },
  { name: 'Cinzel Decorative', file: '/fonts/CinzelDecorative-Regular.woff2', noItalic: true },
  { name: 'ClementePDai', file: '/fonts/ClementePDai-Regular.woff2' },
  { name: 'Comic Neue', file: '/fonts/ComicNeue-Regular.woff2' },
  { name: 'Cormorant', file: '/fonts/Cormorant-Regular.woff2' },
  { name: 'Courier Prime', file: '/fonts/CourierPrime-Regular.woff2' },
  { name: 'Dancing Srcipt', file: '/fonts/DancingScript-Regular.woff2', noItalic: true },
  { name: 'Exo 2', file: '/fonts/Exo2-Regular.woff2' },
  { name: 'Fira Sans', file: '/fonts/FiraSans-Regular.woff2' },
  { name: 'Fira Sans Condensed', file: '/fonts/FiraSansCondensed-Regular.woff2' },
  { name: 'Foldit', file: '/fonts/Foldit-Regular.woff2', noItalic: true },
  { name: 'Fraunces 72pt', file: '/fonts/Fraunces_72pt-Regular.woff2' },
  { name: 'Fredoka', file: '/fonts/Fredoka-Regular.woff2', noItalic: true },
  { name: 'Fredoka Condensed', file: '/fonts/Fredoka_Condensed-Regular.woff2', noItalic: true },
  { name: 'Gluten', file: '/fonts/Gluten-Regular.woff2', noItalic: true },
  { name: 'IBM Plex Serif', file: '/fonts/IBMPlexSerif-Regular.woff2' },
  { name: 'Kalam', file: '/fonts/Kalam-Regular.woff2', noItalic: true },
  { name: 'Libre Baskerville', file: '/fonts/LibreBaskerville-Regular.woff2' },
  { name: 'Libre Franklin', file: '/fonts/LibreFranklin-Regular.woff2' },
  { name: 'Marhey', file: '/fonts/Marhey-Regular.woff2', noItalic: true },
  { name: 'Montserrat', file: '/fonts/Montserrat-Regular.woff2' },
  { name: 'Open sans', file: '/fonts/OpenSans-Regular.woff2' },
  { name: 'Open sans Condensed', file: '/fonts/OpenSans_Condensed-Regular.woff2' },
  { name: 'Passion One', file: '/fonts/PassionOne-Regular.woff2', noItalic: true },
  { name: 'Pixelify sans', file: '/fonts/PixelifySans-Regular.woff2', noItalic: true },
  { name: 'Quantico', file: '/fonts/Quantico-Regular.woff2' },
  { name: 'Roboto Condensed', file: '/fonts/Roboto_Condensed-Regular.woff2' },
  { name: 'Saira Stencil', file: '/fonts/SairaStencil-Regular.woff2' },
  { name: 'Saira Stencil Condensed', file: '/fonts/SairaStencil_Condensed-Regular.woff2' },
  { name: 'Science Gothic', file: '/fonts/ScienceGothic-Regular.woff2', noItalic: true },
  { name: 'Shantell Sans', file: '/fonts/ShantellSans-Regular.woff2' },
  { name: 'Stack Sans Notch', file: '/fonts/StackSansNotch-Regular.woff2', noItalic: true },
  { name: 'Tourney', file: '/fonts/Tourney-Regular.woff2' },
  { name: 'Zilla Slab', file: '/fonts/ZillaSlab-Regular.woff2' },
  { name: 'Bebas Neue', file: '/fonts/BebasNeue-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Josefin Sans', file: '/fonts/JosefinSans-Regular.woff2', ...REGULAR_ONLY },
  { name: 'EB Garamond', file: '/fonts/EBGaramond-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Poppins', file: '/fonts/Poppins-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Figtree', file: '/fonts/Figtree-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Epunda Slab', file: '/fonts/EpundaSlab-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Vidaloka', file: '/fonts/Vidaloka-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Lavishly Yours', file: '/fonts/LavishlyYours-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Miloner', file: '/fonts/MILONER-Medium.woff2', ...REGULAR_ONLY },
  { name: 'Notedry', file: '/fonts/Notedry.woff2', ...REGULAR_ONLY },
  { name: 'Acumin Variable Concept', file: '/fonts/Acumin-Variable-Concept.woff2', ...REGULAR_ONLY },
  { name: 'Bahnschrift', file: '/fonts/BAHNSCHRIFT.woff2', ...REGULAR_ONLY },
  { name: 'Bungee', file: '/fonts/Bungee-Regular.woff2', ...REGULAR_ONLY },
  { name: 'Futura PT', file: '/fonts/FuturaPTMedium.woff2', ...REGULAR_ONLY },
  { name: 'Branch', file: '/fonts/Branch.woff2', ...REGULAR_ONLY },
];

let assetsBaseUrl = '';

/** Définit le préfixe des URLs de polices (ex. 'https://zephir.1year1book.com'). */
export function setPreviewAssetsBaseUrl(base: string): void {
  assetsBaseUrl = base.replace(/\/$/, '');
}

const normalizeFontName = (name: string): string => name.toLocaleLowerCase('en-US');
const FONT_BY_NAME = new Map(PREVIEW_FONTS.map((f) => [normalizeFontName(f.name), f]));
const loaded = new Set<string>();

function findFont(name?: string | null): PreviewFontDef | undefined {
  return name ? FONT_BY_NAME.get(normalizeFontName(name)) : undefined;
}

/** La police supporte-t-elle le gras ? (défaut : oui pour les polices inconnues) */
function fontAllowsBold(name?: string | null): boolean {
  return !findFont(name)?.noBold;
}

/** La police supporte-t-elle l'italique ? (défaut : oui pour les polices inconnues) */
function fontAllowsItalic(name?: string | null): boolean {
  return !findFont(name)?.noItalic;
}

/** Charge (une seule fois) la police demandée. No-op pour les polices système. */
function ensureFontLoaded(name?: string | null): void {
  if (!name || loaded.has(name)) return;
  const def = findFont(name);
  if (!def || !def.file) {
    loaded.add(name);
    return;
  }
  loaded.add(name);
  if (typeof window === 'undefined' || typeof FontFace === 'undefined' || !document.fonts) return;
  try {
    const face = new FontFace(name, `url(${assetsBaseUrl}${def.file}) format('woff2')`, { display: 'swap' });
    face.load().then((f) => document.fonts.add(f)).catch(() => {
      loaded.delete(name);
    });
  } catch {
    loaded.delete(name);
  }
}

// ============================ Rendu des éléments ============================

/** Icône image inline (aucune dépendance lucide-react requise). */
const ImagePlaceholderIcon: React.FC<{ size: number; opacity?: number }> = ({ size, opacity = 0.5 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#9ca3af"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ opacity }}
  >
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

/**
 * Sticker SVG — avec `color` : rendu via CSS `mask-image` teinté (évite les
 * soucis CORS d'un fetch cross-origin) ; sans `color` : simple `<img>`.
 */
const SvgSticker: React.FC<{ url: string; color?: string }> = ({ url, color }) => {
  if (color) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: color,
          WebkitMaskImage: `url("${url}")`,
          maskImage: `url("${url}")`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
        }}
      />
    );
  }
  return (
    <img
      src={url}
      alt=""
      draggable={false}
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
    />
  );
};

/** Rendu lecture seule d'un élément de page (styles inline uniquement). */
const PreviewElementView: React.FC<{ element: PreviewElement }> = ({ element }) => {
  const offset = element.imageOffset || { x: 50, y: 50 };

  if (element.type === 'text') ensureFontLoaded(element.textStyle?.fontFamily);

  const zIndex = element.zIndex != null ? element.zIndex : (element.zPriority === 'front' ? 10 : element.zPriority === 'back' ? 0 : 1);

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.position.x,
    top: element.position.y,
    width: element.size.width,
    height: element.size.height,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    pointerEvents: 'none',
    backgroundColor: element.backgroundColor || undefined,
    zIndex,
    border: element.borderColor ? `1px solid ${element.borderColor}` : undefined,
    boxShadow: elementShadowToCss(element.shadow),
  };

  if (element.type === 'text') {
    return (
      <div
        style={{
          ...baseStyle,
          fontSize: element.textStyle?.fontSize,
          fontFamily: element.textStyle?.fontFamily,
          fontWeight: fontAllowsBold(element.textStyle?.fontFamily) ? element.textStyle?.fontWeight : '400',
          color: element.textStyle?.color,
          textAlign: element.textStyle?.textAlign,
          fontStyle: element.textStyle?.italic && fontAllowsItalic(element.textStyle?.fontFamily) ? 'italic' : undefined,
          textDecoration: element.textStyle?.underline ? 'underline' : undefined,
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.4,
        }}
      >
        {element.content}
      </div>
    );
  }

  if (element.type === 'image') {
    const src = element.imageUrl || element.thumbnail;
    if (!src) return null;
    const isSvgSticker = element.isSticker && (element.stickerFormat === 'svg' || element.stickerFormat === 'simple_svg');
    return (
      <div
        style={{
          ...baseStyle,
          overflow: 'hidden',
          borderRadius: cornerRadiiToCss(element.cornerRadii) || (element.isCircle ? '50%' : element.borderRadius ? `${element.borderRadius}px` : 2),
        }}
      >
        {isSvgSticker ? (
          <SvgSticker url={src} color={element.stickerColor} />
        ) : (
          <img
            src={src}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: element.forceDimension ? 'fill' : 'cover',
              objectPosition: `${offset.x}% ${offset.y}%`,
            }}
            draggable={false}
          />
        )}
      </div>
    );
  }

  if (element.type === 'free_slot') {
    const circleStyle: React.CSSProperties = element.isCircle
      ? { borderRadius: '50%' }
      : cornerRadiiToCss(element.cornerRadii)
        ? { borderRadius: cornerRadiiToCss(element.cornerRadii) }
        : {};
    return (
      <div
        style={{
          ...baseStyle,
          ...circleStyle,
          border: '2px dashed rgba(156,163,175,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ImagePlaceholderIcon size={24} opacity={0.4} />
      </div>
    );
  }

  if (element.type === 'slot') {
    const slotSrc = element.previewImageUrl || element.imageUrl;
    const circleStyle: React.CSSProperties = element.isCircle
      ? { borderRadius: '50%' }
      : cornerRadiiToCss(element.cornerRadii)
        ? { borderRadius: cornerRadiiToCss(element.cornerRadii) }
        : element.borderRadius
          ? { borderRadius: `${element.borderRadius}px` }
          : {};
    if (slotSrc) {
      return (
        <div style={{ ...baseStyle, ...circleStyle, overflow: 'hidden' }}>
          <img
            src={slotSrc}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: `${offset.x}% ${offset.y}%`,
              transform: element.imageScale && element.imageScale > 1 ? `scale(${element.imageScale})` : undefined,
              transformOrigin: `${offset.x}% ${offset.y}%`,
            }}
            draggable={false}
          />
        </div>
      );
    }
    return (
      <div
        style={{
          ...baseStyle,
          ...circleStyle,
          border: '2px dashed rgba(156,163,175,0.5)',
          borderRadius: circleStyle.borderRadius ?? 2,
          backgroundColor: 'rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ImagePlaceholderIcon size={Math.max(10, Math.min(element.size.width, element.size.height) * 0.3)} />
      </div>
    );
  }

  if (element.type === 'shape') {
    const color = element.textStyle?.color || '#000000';
    const w = element.size.width;
    const h = element.size.height;
    return (
      <div style={{ ...baseStyle, borderRadius: cornerRadiiToCss(element.cornerRadii) || undefined }}>
        <svg width={w} height={h}>
          {element.shapeType === 'circle' && <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill={color} />}
          {element.shapeType === 'line' && <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke={color} strokeWidth={element.strokeWidth ?? 2} />}
          {(!element.shapeType || element.shapeType === 'rect') && <rect width={w} height={h} fill={color} />}
        </svg>
      </div>
    );
  }

  if (element.type === 'shape_border') {
    return (
      <div
        style={{
          ...baseStyle,
          border: `2px solid ${element.textStyle?.color || '#000000'}`,
          borderRadius: cornerRadiiToCss(element.cornerRadii) || undefined,
        }}
      />
    );
  }

  return null;
};

// ============================== Composant ==============================

export interface CoverPreviewProps {
  /**
   * Page complète à afficher (1ère de couverture). Accepte l'objet normalisé
   * (background + elements) ou le JSON brut Rails (objects_position,
   * snake_case) — la normalisation est faite en interne.
   */
  page: PreviewPage | any;
  /**
   * Domaine du serveur Rails hébergeant les polices woff2, ex.
   * 'https://zephir.1year1book.com'. À fournir quand le composant tourne sur
   * un autre domaine que celui qui sert les /fonts/*.
   */
  assetsBaseUrl?: string;
  /** Ratio largeur / hauteur. Ex. : 1 carré, 0.7 portrait, 1.4 paysage. */
  aspectRatio?: number;
  /** Largeur réelle de la page en pt (page_width du load_book). Prioritaire sur toute déduction. */
  pageWidth?: number;
  /** Hauteur réelle de la page en pt (page_height du load_book). Prioritaire sur aspectRatio. */
  pageHeight?: number;
  /** Hauteur maximale en px (défaut : hauteur disponible dans le parent). */
  maxHeight?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Aperçu d'une page de yearbook (typiquement la 1ère de couverture).
 *
 * - S'adapte automatiquement à la largeur et à la hauteur disponibles dans
 *   son parent (ResizeObserver), sans jamais déborder de son cadre.
 * - Styles inline uniquement : aucune dépendance Tailwind/shadcn, copiable
 *   tel quel dans un autre projet.
 */
const CoverPreview: React.FC<CoverPreviewProps> = ({ page, assetsBaseUrl, aspectRatio, pageWidth: pageWidthProp, pageHeight: pageHeightProp, maxHeight, className, style }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const normalized = useMemo(() => normalizePage(page), [page]);
  // Largeur de référence : page_width explicite, sinon déduite de l'étendue
  // des éléments (une couverture A4 paysage est composée sur 842 pt, pas 595).
  const pageW = useMemo(() => {
    if (pageWidthProp && pageWidthProp > 0) return pageWidthProp;
    if (normalized.pageWidth) return normalized.pageWidth;
    const extent = normalized.elements.reduce((m, el: any) => {
      const w = el?.size?.width ?? 0;
      const x = el?.position?.x ?? 0;
      // ignore les éléments qui débordent volontairement (50 % max)
      return Math.max(m, x + w * 0.5);
    }, 0);
    const candidates = [PREVIEW_PAGE_WIDTH, PREVIEW_PAGE_HEIGHT, 1191];
    return candidates.find((c) => extent <= c) ?? candidates[candidates.length - 1];
  }, [pageWidthProp, normalized.pageWidth, normalized.elements]);
  const validAspectRatio = typeof aspectRatio === 'number' && Number.isFinite(aspectRatio) && aspectRatio > 0
    ? aspectRatio
    : undefined;
  const pageH = pageHeightProp && pageHeightProp > 0
    ? pageHeightProp
    : validAspectRatio ? pageW / validAspectRatio : PREVIEW_PAGE_HEIGHT;

  useEffect(() => {
    if (assetsBaseUrl) setPreviewAssetsBaseUrl(assetsBaseUrl);
  }, [assetsBaseUrl]);

  const computeScale = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const parent = el.parentElement;
    const ownStyles = window.getComputedStyle(el);
    const parentStyles = parent ? window.getComputedStyle(parent) : undefined;
    const ownMaxHeight = Number.parseFloat(ownStyles.maxHeight);
    const parentHeight = parent
      ? parent.clientHeight
        - Number.parseFloat(parentStyles?.paddingTop || '0')
        - Number.parseFloat(parentStyles?.paddingBottom || '0')
      : 0;

    const availableHeights = [maxHeight, ownMaxHeight, parentHeight]
      .filter((height): height is number => typeof height === 'number' && Number.isFinite(height) && height > 0);
    const availableHeight = availableHeights.length > 0 ? Math.min(...availableHeights) : undefined;
    const widthScale = el.clientWidth / pageW;
    const heightScale = availableHeight ? availableHeight / pageH : Number.POSITIVE_INFINITY;
    const nextScale = Math.min(widthScale, heightScale);

    if (Number.isFinite(nextScale) && nextScale > 0) setScale(nextScale);
  }, [pageW, pageH, maxHeight]);

  useEffect(() => {
    computeScale();
    const obs = new ResizeObserver(computeScale);
    if (containerRef.current) obs.observe(containerRef.current);
    if (containerRef.current?.parentElement) obs.observe(containerRef.current.parentElement);
    return () => obs.disconnect();
  }, [computeScale]);

  const bg = normalized.background;
  const bgIsImage = bg.type !== 'color';
  const hasUserBg = bgIsImage && (bg.trame_background_id != null || bg.trame_background_custom_id != null);
  const bgInset = hasUserBg ? PREVIEW_BG_BLEED : 0;

  const sortedElements = useMemo(() => {
    const zOrder = (el: { zIndex?: number; zPriority?: 'front' | 'back' }) =>
      el.zIndex != null ? el.zIndex : el.zPriority === 'back' ? -1 : el.zPriority === 'front' ? 1 : 0;
    return [...normalized.elements].sort((a, b) => zOrder(a) - zOrder(b));
  }, [normalized.elements]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden', ...style }}
    >
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          width: pageW * scale,
          height: pageH * scale,
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
          borderRadius: 2,
        }}
      >
        {bgIsImage && (
          <img
            src={bg.value}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              top: -bgInset * scale,
              left: -bgInset * scale,
              width: (pageW + bgInset * 2) * scale,
              height: (pageH + bgInset * 2) * scale,
              maxWidth: 'none',
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
          />
        )}
        <div
          style={{
            position: 'relative',
            width: pageW,
            height: pageH,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            overflow: 'hidden',
            backgroundColor: bgIsImage ? undefined : bg.value,
          }}
        >
          {sortedElements.map((el) => (
            <PreviewElementView key={el.id} element={el} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoverPreview;